import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Users, UserCheck, UserX, AlertTriangle, Briefcase, TrendingUp, ClipboardList, GraduationCap, Edit } from 'lucide-react';
import { BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LineChart, Line } from 'recharts';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Overview() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    latestReport: null,
    totalStudentsToday: 0,
    teachersPresentToday: 0,
    teachersAbsentToday: 0,
    disciplinaryCount: 0,
    jobApplicationsCount: 0,
    recentReports: [],
  });
  const [enrollment, setEnrollment] = useState(0);
  const [editEnrollmentOpen, setEditEnrollmentOpen] = useState(false);
  const [enrollmentInput, setEnrollmentInput] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
    fetchEnrollment();
  }, []);

  const fetchEnrollment = async () => {
    try {
      if (user.role === 'admin' || user.role === 'teacher') {
        const { data } = await axios.get(`${BACKEND_URL}/api/settings/enrollment`, {
          withCredentials: true,
        });
        setEnrollment(data.total_enrollment);
        setEnrollmentInput(data.total_enrollment.toString());
      }
    } catch (error) {
      console.error('Error fetching enrollment:', error);
    }
  };

  const handleUpdateEnrollment = async () => {
    try {
      const newEnrollment = parseInt(enrollmentInput) || 0;
      await axios.put(
        `${BACKEND_URL}/api/settings/enrollment`,
        { total_enrollment: newEnrollment },
        { withCredentials: true }
      );
      setEnrollment(newEnrollment);
      setEditEnrollmentOpen(false);
      toast.success('Total enrollment updated successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update enrollment');
    }
  };

  const fetchStats = async () => {
    try {
      const requests = [
        axios.get(`${BACKEND_URL}/api/disciplinary`, { withCredentials: true }),
      ];

      // Add daily reports for admin and teachers
      if (user.role === 'admin' || user.role === 'teacher') {
        requests.push(
          axios.get(`${BACKEND_URL}/api/attendance/daily-reports?limit=7`, { withCredentials: true })
        );
      }

      // Add job applications for admin
      if (user.role === 'admin') {
        requests.push(
          axios.get(`${BACKEND_URL}/api/job-applications`, { withCredentials: true })
        );
      }

      const responses = await Promise.all(requests);
      const disciplinaryRes = responses[0];
      
      let dailyReports = [];
      let jobsData = [];
      
      if (user.role === 'admin' || user.role === 'teacher') {
        dailyReports = responses[1].data;
      }
      
      if (user.role === 'admin') {
        jobsData = responses[responses.length - 1].data;
      }

      // Get latest report (today or most recent)
      const latestReport = dailyReports[0] || null;
      let totalStudents = 0;
      
      if (latestReport) {
        // Calculate total students from all grades
        Object.keys(latestReport).forEach(key => {
          if (key.startsWith('grade_')) {
            totalStudents += latestReport[key].morning.total + latestReport[key].afternoon.total;
          }
        });
      }

      setStats({
        latestReport,
        totalStudentsToday: totalStudents,
        teachersPresentToday: latestReport?.teachers?.present || 0,
        teachersAbsentToday: latestReport?.teachers?.absent || 0,
        teachersTotalToday: latestReport?.teachers?.total_teachers || 0,
        disciplinaryCount: disciplinaryRes.data.length,
        jobApplicationsCount: jobsData.length || 0,
        recentReports: dailyReports,
      });
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const pieData = [
    { name: 'Present', value: stats.presentCount, color: '#10B981' },
    { name: 'Absent', value: stats.absentCount, color: '#F59E0B' },
  ];

  if (loading) {
    return <div>Loading...</div>;
  }

  // For students, show simple message
  if (user.role === 'student') {
    return (
      <div data-testid="overview-page">
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-[#0F172A] tracking-tight" style={{ fontFamily: 'Manrope' }}>
            Welcome back, {user?.name}
          </h1>
          <p className="text-[#64748B] mt-2">Student Dashboard</p>
        </div>

        <Card className="stat-card" data-testid="stat-card-disciplinary">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-[#64748B]">My Disciplinary Cases</CardTitle>
            <AlertTriangle className="w-4 h-4 text-[#EF4444]" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-[#EF4444]" style={{ fontFamily: 'Manrope' }}>
              {stats.disciplinaryCount}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Prepare chart data for weekly trend
  const weeklyData = stats.recentReports.slice(0, 7).reverse().map(report => {
    let totalStudents = 0;
    Object.keys(report).forEach(key => {
      if (key.startsWith('grade_')) {
        totalStudents += report[key].morning.total + report[key].afternoon.total;
      }
    });
    return {
      date: report.date,
      students: totalStudents,
      teachers: report.teachers.present
    };
  });

  // Prepare grade breakdown data
  const gradeBreakdown = stats.latestReport ? [
    { grade: 7, data: stats.latestReport.grade_7 },
    { grade: 8, data: stats.latestReport.grade_8 },
    { grade: 9, data: stats.latestReport.grade_9 },
    { grade: 10, data: stats.latestReport.grade_10 },
    { grade: 11, data: stats.latestReport.grade_11 },
  ] : [];

  return (
    <div data-testid="overview-page" className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-[#1A202C]" style={{ fontFamily: 'Manrope' }}>
            Dashboard
          </h1>
          <p className="text-[#718096] mt-1">Secure • Organized • Accessible</p>
        </div>
      </div>

      {/* Greeting Card */}
      <Card className="bg-gradient-to-r from-[#1E3A8A] to-[#1E40AF] border-none shadow-xl">
        <CardContent className="p-8">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-3xl font-bold text-white mb-2" style={{ fontFamily: 'Manrope' }}>
                Good {new Date().getHours() < 12 ? 'Morning' : new Date().getHours() < 18 ? 'Afternoon' : 'Evening'} 👋
              </h2>
              <p className="text-blue-100 text-lg">Welcome back, {user?.name}</p>
            </div>
            <div className="text-right">
              <p className="text-blue-100 text-sm">{new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</p>
              <p className="text-white text-2xl font-bold mt-1">{new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Main Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {/* Students Card - Blue */}
        <Card className="bg-gradient-to-br from-[#3B82F6] to-[#2563EB] border-none shadow-lg hover:shadow-2xl transition-all cursor-pointer" onClick={() => navigate('/dashboard/daily-attendance')}>
          <CardContent className="p-6">
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-2">
                <Users className="w-5 h-5 text-white" />
                <span className="text-white font-semibold">Students</span>
              </div>
              <span className="px-3 py-1 bg-white/20 rounded-full text-xs text-white font-semibold">
                Today
              </span>
            </div>
            <div className="text-5xl font-bold text-white mb-2" style={{ fontFamily: 'Manrope' }}>
              {stats.totalStudentsToday}
            </div>
            <p className="text-blue-100 text-sm">Total Students</p>
          </CardContent>
        </Card>

        {/* Teachers Card - Green */}
        <Card className="bg-gradient-to-br from-[#10B981] to-[#059669] border-none shadow-lg hover:shadow-2xl transition-all cursor-pointer" onClick={() => navigate('/dashboard/daily-attendance')}>
          <CardContent className="p-6">
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-2">
                <UserCheck className="w-5 h-5 text-white" />
                <span className="text-white font-semibold">Teachers</span>
              </div>
              <span className="px-3 py-1 bg-white/20 rounded-full text-xs text-white font-semibold">
                {stats.teachersTotalToday > 0 ? Math.round((stats.teachersPresentToday / stats.teachersTotalToday) * 100) : 0}% Present
              </span>
            </div>
            <div className="text-5xl font-bold text-white mb-2" style={{ fontFamily: 'Manrope' }}>
              {stats.teachersPresentToday}
            </div>
            <p className="text-emerald-100 text-sm">Total Teachers</p>
          </CardContent>
        </Card>

        {/* Discipline Card - Orange */}
        <Card className="bg-gradient-to-br from-[#F59E0B] to-[#D97706] border-none shadow-lg hover:shadow-2xl transition-all cursor-pointer">
          <CardContent className="p-6">
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-white" />
                <span className="text-white font-semibold">Discipline</span>
              </div>
              <span className="px-3 py-1 bg-white/20 rounded-full text-xs text-white font-semibold">
                Active
              </span>
            </div>
            <div className="text-5xl font-bold text-white mb-2" style={{ fontFamily: 'Manrope' }}>
              {stats.disciplinaryCount}
            </div>
            <p className="text-orange-100 text-sm">Active Cases</p>
          </CardContent>
        </Card>

        {/* Applications Card - Purple */}
        {user.role === 'admin' && (
          <Card className="bg-gradient-to-br from-[#8B5CF6] to-[#7C3AED] border-none shadow-lg hover:shadow-2xl transition-all cursor-pointer" onClick={() => navigate('/dashboard/jobs')}>
            <CardContent className="p-6">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Briefcase className="w-5 h-5 text-white" />
                  <span className="text-white font-semibold">Applications</span>
                </div>
                <span className="px-3 py-1 bg-white/20 rounded-full text-xs text-white font-semibold">
                  {stats.jobApplicationsCount} Pending
                </span>
              </div>
              <div className="text-5xl font-bold text-white mb-2" style={{ fontFamily: 'Manrope' }}>
                {stats.jobApplicationsCount}
              </div>
              <p className="text-purple-100 text-sm">Employment Applications</p>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Total Enrollment Card */}
      {(user.role === 'admin' || user.role === 'teacher') && (
        <Card className="shadow-lg border-none">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-14 h-14 bg-gradient-to-br from-[#667EEA] to-[#764BA2] rounded-xl flex items-center justify-center shadow-lg">
                  <Users className="w-7 h-7 text-white" />
                </div>
                <div>
                  <CardTitle className="text-xl text-[#1A202C]">Total School Enrollment</CardTitle>
                  <CardDescription>Official registered student count</CardDescription>
                </div>
              </div>
              {user.role === 'admin' && (
                <Dialog open={editEnrollmentOpen} onOpenChange={setEditEnrollmentOpen}>
                  <DialogTrigger asChild>
                    <Button variant="outline" size="sm" data-testid="edit-enrollment-btn" className="border-gray-300">
                      <Edit className="w-4 h-4 mr-2" />
                      Edit
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Update Total Enrollment</DialogTitle>
                      <DialogDescription>
                        Enter the official total number of students enrolled in the school
                      </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                      <div className="space-y-2">
                        <Label htmlFor="enrollment">Total Enrollment</Label>
                        <Input
                          id="enrollment"
                          type="number"
                          min="0"
                          data-testid="enrollment-input"
                          value={enrollmentInput}
                          onChange={(e) => setEnrollmentInput(e.target.value)}
                          placeholder="Enter total number of students"
                        />
                      </div>
                    </div>
                    <div className="flex justify-end gap-2">
                      <Button variant="outline" onClick={() => setEditEnrollmentOpen(false)}>
                        Cancel
                      </Button>
                      <Button 
                        onClick={handleUpdateEnrollment}
                        data-testid="save-enrollment-btn"
                        className="bg-[#3B82F6] hover:bg-[#2563EB]"
                      >
                        Save
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>
              )}
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-6xl font-bold bg-gradient-to-r from-[#667EEA] to-[#764BA2] bg-clip-text text-transparent" style={{ fontFamily: 'Manrope' }} data-testid="enrollment-display">
              {enrollment.toLocaleString()}
            </div>
            <p className="text-sm text-[#718096] mt-2 font-medium">students</p>
          </CardContent>
        </Card>
      )}

      {/* Grade-by-Grade Student Attendance */}
      {stats.latestReport && (
        <>
          <div className="mb-4">
            <h2 className="text-xl font-bold text-[#1A202C]">Student Attendance by Grade</h2>
            <p className="text-sm text-[#718096]">as of {stats.latestReport.date}</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-5">
            {gradeBreakdown.map(({ grade, data }) => {
              return (
                <Card 
                  key={grade} 
                  className="shadow-lg border-none hover:shadow-xl cursor-pointer transition-all bg-white"
                  onClick={() => navigate('/dashboard/daily-attendance')}
                  data-testid={`stat-card-grade-${grade}`}
                >
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between mb-5">
                      <span className="text-[#1A202C] font-bold text-lg">Grade {grade}</span>
                      <GraduationCap className="w-5 h-5 text-[#667EEA]" />
                    </div>
                    
                    {/* Morning Session Box */}
                    <div className="mb-4 p-4 rounded-lg bg-gradient-to-br from-blue-50 to-blue-100 border border-blue-200">
                      <div className="text-xs font-semibold text-blue-900 uppercase tracking-wide mb-3">
                        Morning Session
                      </div>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between items-center">
                          <span className="text-blue-700">Boys:</span>
                          <span className="font-bold text-blue-900">{data.morning.boys}</span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-blue-700">Girls:</span>
                          <span className="font-bold text-blue-900">{data.morning.girls}</span>
                        </div>
                        <div className="flex justify-between items-center pt-2 border-t border-blue-200">
                          <span className="font-semibold text-blue-900">Total:</span>
                          <span className="font-bold text-blue-900 text-lg">{data.morning.total}</span>
                        </div>
                      </div>
                    </div>

                    {/* Afternoon Session Box */}
                    <div className="p-4 rounded-lg bg-gradient-to-br from-purple-50 to-purple-100 border border-purple-200">
                      <div className="text-xs font-semibold text-purple-900 uppercase tracking-wide mb-3">
                        Afternoon Session
                      </div>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between items-center">
                          <span className="text-purple-700">Boys:</span>
                          <span className="font-bold text-purple-900">{data.afternoon.boys}</span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-purple-700">Girls:</span>
                          <span className="font-bold text-purple-900">{data.afternoon.girls}</span>
                        </div>
                        <div className="flex justify-between items-center pt-2 border-t border-purple-200">
                          <span className="font-semibold text-purple-900">Total:</span>
                          <span className="font-bold text-purple-900 text-lg">{data.afternoon.total}</span>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </>
      )}

      {/* Teacher Stats and Disciplinary */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">

        <Card className="stat-card cursor-pointer hover:shadow-md transition-shadow bg-gradient-to-br from-emerald-50 to-emerald-100" onClick={() => navigate('/dashboard/daily-attendance')} data-testid="stat-card-teachers-present">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-[#64748B]">Teachers Present</CardTitle>
            <UserCheck className="w-4 h-4 text-[#10B981]" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-[#10B981]" style={{ fontFamily: 'Manrope' }}>
              {stats.teachersPresentToday}
            </div>
            <p className="text-xs text-[#64748B] mt-1">
              out of {stats.teachersTotalToday} teachers
            </p>
          </CardContent>
        </Card>

        <Card className="stat-card cursor-pointer hover:shadow-md transition-shadow bg-gradient-to-br from-amber-50 to-amber-100" onClick={() => navigate('/dashboard/daily-attendance')} data-testid="stat-card-teachers-absent">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-[#64748B]">Teachers Absent</CardTitle>
            <UserX className="w-4 h-4 text-[#F59E0B]" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-[#F59E0B]" style={{ fontFamily: 'Manrope' }}>
              {stats.teachersAbsentToday}
            </div>
          </CardContent>
        </Card>

        <Card className="stat-card bg-gradient-to-br from-rose-50 to-rose-100" data-testid="stat-card-disciplinary">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-[#64748B]">Disciplinary Cases</CardTitle>
            <AlertTriangle className="w-4 h-4 text-[#EF4444]" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-[#EF4444]" style={{ fontFamily: 'Manrope' }}>
              {stats.disciplinaryCount}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {weeklyData.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle>Weekly Attendance Trend</CardTitle>
              <CardDescription>Last 7 days attendance overview</CardDescription>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={weeklyData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis dataKey="date" stroke="#64748B" fontSize={12} />
                  <YAxis stroke="#64748B" fontSize={12} />
                  <Tooltip />
                  <Legend />
                  <Line type="monotone" dataKey="students" stroke="#3B82F6" name="Students" strokeWidth={2} />
                  <Line type="monotone" dataKey="teachers" stroke="#10B981" name="Teachers" strokeWidth={2} />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        )}

        {user.role === 'admin' && (
          <Card>
            <CardHeader>
              <CardTitle>Job Applications</CardTitle>
              <CardDescription>Total applications received</CardDescription>
            </CardHeader>
            <CardContent className="flex items-center justify-center h-[300px]">
              <div className="text-center">
                <Briefcase className="w-16 h-16 text-[#3B82F6] mx-auto mb-4" />
                <div className="text-5xl font-bold text-[#0F172A]" style={{ fontFamily: 'Manrope' }}>
                  {stats.jobApplicationsCount}
                </div>
                <p className="text-[#64748B] mt-2">Applications</p>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Quick Action Card */}
      {!stats.latestReport && (user.role === 'admin' || user.role === 'teacher') && (
        <Card className="bg-gradient-to-br from-[#3B82F6] to-[#2563EB] text-white mb-6">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <ClipboardList className="w-5 h-5" />
              Submit Today's Attendance
            </CardTitle>
            <CardDescription className="text-blue-100">No attendance report submitted for today yet</CardDescription>
          </CardHeader>
          <CardContent>
            <button
              onClick={() => navigate('/dashboard/daily-attendance')}
              className="inline-flex items-center px-6 py-3 bg-white text-[#3B82F6] hover:bg-blue-50 rounded-md font-medium transition-colors"
              data-testid="submit-attendance-btn"
            >
              Submit Daily Report
            </button>
          </CardContent>
        </Card>
      )}

      {/* Quick Actions */}
      {user.role === 'admin' && (
        <Card className="bg-gradient-to-br from-[#0F172A] to-[#1E293B] text-white">
          <CardHeader>
            <CardTitle className="text-white">Accept Job Applications</CardTitle>
            <CardDescription className="text-gray-300">Share this link with potential applicants</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-4">
              <div className="flex-1 bg-white/10 backdrop-blur-sm rounded-md px-4 py-3 font-mono text-sm">
                {window.location.origin}/apply
              </div>
              <a
                href="/apply"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center px-6 py-3 bg-white text-[#0F172A] hover:bg-gray-100 rounded-md font-medium transition-colors"
                data-testid="open-application-form-btn"
              >
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                </svg>
                Open Form
              </a>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
